import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";

/* ─── Avatar colour palette ─────────────────────────────────── */

const companyColors = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-purple-100 text-purple-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-cyan-100 text-cyan-700",
  "bg-indigo-100 text-indigo-700",
  "bg-orange-100 text-orange-700",
];

function getCompanyColor(name: string | undefined | null) {
  if (!name) return companyColors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return companyColors[Math.abs(hash) % companyColors.length];
}

/* ─── Domain lookup fallback for callers that only pass `name` ─ */

const companyDomains: Record<string, string> = {
  "Stripe": "stripe.com",
  "Shopify": "shopify.com",
  "HubSpot": "hubspot.com",
  "Datadog": "datadoghq.com",
  "Notion": "notion.so",
  "Figma": "figma.com",
  "Canva": "canva.com",
  "Plaid": "plaid.com",
  "Linear": "linear.app",
  "Airtable": "airtable.com",
  "Vercel": "vercel.com",
  "Supabase": "supabase.com",
  "Anthropic": "anthropic.com",
  "Zendesk": "zendesk.com",
  "Twilio": "twilio.com",
  "MongoDB": "mongodb.com",
  "Cloudflare": "cloudflare.com",
  "Amplitude": "amplitude.com",
  "Webflow": "webflow.com",
  "Contentful": "contentful.com",
  "GitLab": "gitlab.com",
  "Segment": "segment.com",
  "Intercom": "intercom.com",
  "Loom": "loom.com",
  "Miro": "miro.com",
  "Snowflake": "snowflake.com",
  "PagerDuty": "pagerduty.com",
  "Confluent": "confluent.io",
  "Elastic": "elastic.co",
  "Okta": "okta.com",
  "Splunk": "splunk.com",
  "Atlassian": "atlassian.com",
  "Postman": "postman.com",
  "CircleCI": "circleci.com",
  "HashiCorp": "hashicorp.com",
  "LaunchDarkly": "launchdarkly.com",
  "Sentry": "sentry.io",
  "Algolia": "algolia.com",
  "Cypress": "cypress.io",
  "Zapier": "zapier.com",
  "Mixpanel": "mixpanel.com",
  "Grafana": "grafana.com",
  "Retool": "retool.com",
  "Brex": "brex.com",
  "Deel": "deel.com",
  "Rippling": "rippling.com",
  "Carta": "carta.com",
  "Neon": "neon.tech",
  "Prisma": "prisma.io",
  "Render": "render.com",
  "Weights & Biases": "wandb.ai",
};

/* ─── URL helpers ────────────────────────────────────────────── */

/** Strips protocol/www so both "stripe.com" and "https://www.stripe.com" work. */
function normalizeDomain(raw: string): string {
  try {
    const withProto = raw.includes("://") ? raw : `https://${raw}`;
    return new URL(withProto).hostname.replace(/^www\./, "");
  } catch {
    return raw;
  }
}

function coffeeLogoUrl(domain: string): string {
  return `https://coffee-logos.s3.us-west-2.amazonaws.com/company/${normalizeDomain(domain)}.png`;
}

function googleFaviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${normalizeDomain(domain)}&sz=64`;
}

/* ─── Stage machine: coffee → favicon → avatar ───────────────── */

type Stage = "coffee" | "favicon" | "avatar";

function initialStage(effectiveDomain: string | null): Stage {
  return effectiveDomain ? "coffee" : "avatar";
}

/* ─── Component ─────────────────────────────────────────────── */

export interface CompanyLogoProps {
  /** Company display name — used for the avatar fallback and domain lookup */
  name?: string | null;
  /** Primary domain (e.g. "stripe.com"). When omitted, falls back to the
   *  built-in name→domain map, then to the name avatar. */
  domain?: string | null;
  size?: "sm" | "md" | "lg";
}

export default function CompanyLogo({ name, domain, size = "sm" }: CompanyLogoProps) {
  // Resolve the best available domain
  const effectiveDomain: string | null =
    domain || (name ? (companyDomains[name] ?? null) : null);

  const [stage, setStage] = useState<Stage>(() => initialStage(effectiveDomain));

  // Reset the stage whenever the resolved domain changes (e.g. async data load)
  useEffect(() => {
    setStage(initialStage(effectiveDomain));
  }, [effectiveDomain]);

  const sizeClasses =
    size === "lg" ? "h-12 w-12" : size === "md" ? "h-8 w-8" : "h-6 w-6";
  const textSize = size === "lg" ? "text-xl" : size === "md" ? "text-xs" : "text-[10px]";
  const displayName = name || "?";

  /* Special case: Gazillion (preview workspace) uses local asset to match sidebar */
  if (name === "Gazillion") {
    return (
      <div
        className={cn(
          sizeClasses,
          "rounded-lg flex items-center justify-center shrink-0 overflow-hidden bg-white"
        )}
      >
        <img
          src="/logos/gazillion-icon.jpg"
          alt={displayName}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  /* Stage 1: Coffee S3 logo */
  if (stage === "coffee" && effectiveDomain) {
    return (
      <div
        className={cn(
          sizeClasses,
          "rounded-lg flex items-center justify-center shrink-0 overflow-hidden bg-white"
        )}
      >
        <img
          src={coffeeLogoUrl(effectiveDomain)}
          alt={displayName}
          className="h-full w-full object-contain p-0.5"
          onError={() => setStage("favicon")}
        />
      </div>
    );
  }

  /* Stage 2: Google favicon (only reached after Coffee S3 fails) */
  if (stage === "favicon" && effectiveDomain) {
    return (
      <div
        className={cn(
          sizeClasses,
          "rounded-lg flex items-center justify-center shrink-0 overflow-hidden bg-white"
        )}
      >
        <img
          src={googleFaviconUrl(effectiveDomain)}
          alt={displayName}
          className="h-full w-full object-contain p-0.5"
          onError={() => setStage("avatar")}
        />
      </div>
    );
  }

  /* Stage 3: Name initial avatar */
  return (
    <div
      className={cn(
        sizeClasses,
        "rounded-lg flex items-center justify-center font-bold shrink-0",
        textSize,
        getCompanyColor(name)
      )}
    >
      {displayName.charAt(0).toUpperCase()}
    </div>
  );
}
